import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, CheckSquare, Square, type LucideIcon, ChevronDown } from 'lucide-react';

interface DropdownOption {
    label: string;
    value: string;
}

interface MultiSelectDropdownProps {
    label?: string;
    options: DropdownOption[];
    value?: string[];
    onChange: (values: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    icon?: LucideIcon | null;
    searchInputPlaceholder?: string;
    error?: string;
    required?: boolean;
    maxVisibleTags?: number;
    width?: string;
    variant?: 'default' | 'project';
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label,
    options = [],
    value = [],
    onChange,
    disabled = false,
    placeholder = "Select items",
    className = "",
    icon: Icon = null,
    searchInputPlaceholder = 'Type to search...',
    error,
    required,
    maxVisibleTags = 2,
    width = "w-full",
    variant = "default"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 280;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let position: 'bottom' | 'top' = 'bottom';
        let style: React.CSSProperties = {
            width: `${buttonRect.width}px`,
            left: `${buttonRect.left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                (!menuRef.current || !menuRef.current.contains(target))
            ) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (showSearch && isOpen && searchInputRef.current) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showSearch, isOpen]);

    const toggleDropdown = () => {
        if (!disabled) {
            calculateDropdownPosition();
            setIsOpen(!isOpen);
            if (isOpen) {
                setShowSearch(false);
                setSearchTerm('');
            }
        }
    };

    const handleItemToggle = (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const isSelected = value.includes(optionValue);
        let newValues;
        if (isSelected) {
            newValues = value.filter(v => v !== optionValue);
        } else {
            newValues = [...value, optionValue];
        }
        onChange(newValues);
    };

    const handleRemoveTag = (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newValues = value.filter(v => v !== optionValue);
        onChange(newValues);
    };

    const handleClearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const visibleTags = value.slice(0, maxVisibleTags);
    const hiddenTagCount = value.length - maxVisibleTags;

    const getLabel = (val: string) => {
        return options.find(opt => opt.value === val)?.label || val;
    };

    return (
        <div className={`space-y-1.5 ${className} ${width}`} ref={dropdownRef}>
            {label && (
                <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    {Icon && <Icon className="w-3.5 h-3.5 text-orange-500" aria-hidden="true" />}
                    {label}
                    {required && <span className='text-red-500'>*</span>}
                </label>
            )}

            <div className="relative">
                <div
                    ref={buttonRef}
                    onClick={toggleDropdown}
                    className={`relative w-full cursor-pointer rounded-xl border-2 text-left shadow-sm transition-all duration-300 flex items-center
                        ${variant === 'project'
                            ? `bg-slate-50 px-4 py-3 min-h-[48px] ${disabled ? 'border-slate-100' : 'hover:bg-white hover:border-slate-200'} ${isOpen ? 'border-orange-500 bg-white ring-4 ring-orange-500/10' : 'border-slate-100'}`
                            : `bg-slate-50 px-3 py-1.5 min-h-[40px] ${disabled ? 'opacity-50 cursor-not-allowed border-slate-100' : 'hover:bg-white hover:border-slate-200'} ${isOpen ? 'border-orange-500 bg-white ring-4 ring-orange-500/10' : 'border-slate-100'}`
                        }
                        focus:outline-none ${className}`}
                >
                    <div className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0 pr-6">
                        {value.length > 0 ? (
                            <>
                                {visibleTags.map((val) => (
                                    <span
                                        key={val}
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 shadow-sm"
                                    >
                                        <span className="truncate max-w-[100px]">{getLabel(val)}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemoveTag(val, e)}
                                            className="hover:bg-slate-100 rounded-md p-0.5 transition-colors"
                                        >
                                            <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
                                        </button>
                                    </span>
                                ))}
                                {hiddenTagCount > 0 && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 bg-orange-50 border border-orange-100 rounded-lg text-[10px] font-black text-orange-600 uppercase tracking-tight">
                                        +{hiddenTagCount}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className={`block truncate text-slate-400 text-sm font-bold`}>
                                {placeholder}
                            </span>
                        )}
                    </div>

                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1">
                        {value.length > 0 && (
                            <button
                                onClick={handleClearSelection}
                                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                                type="button"
                            >
                                <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                            </button>
                        )}
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-orange-500' : ''}`} />
                    </div>
                </div>

                {isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[99999] max-h-72 overflow-hidden rounded-xl bg-white shadow-2xl border border-slate-200 animate-in fade-in-0 zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                        style={{ ...menuStyle }}
                    >
                        <div className="flex flex-col h-full">
                            <div className="p-2 border-b border-slate-100 bg-slate-50/50">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm text-slate-800 font-bold placeholder-slate-400 focus:border-orange-500 focus:outline-none transition-all"
                                        placeholder={searchInputPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </div>

                            <div className="overflow-y-auto custom-scroll flex-1 max-h-[200px]">
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((opt) => {
                                        const isSelected = value.includes(opt.value);
                                        return (
                                            <div
                                                key={opt.value}
                                                onClick={(e) => handleItemToggle(opt.value, e)}
                                                className={`w-full cursor-pointer py-2.5 px-3 flex items-start gap-2.5 text-sm font-bold transition-all duration-150 hover:bg-slate-50 ${isSelected ? "bg-orange-50 text-orange-600" : "text-slate-600"}`}
                                            >
                                                <div className="flex-shrink-0 pt-0.5">
                                                    {isSelected ? (
                                                        <CheckSquare className="w-4 h-4 text-orange-600" />
                                                    ) : (
                                                        <Square className="w-4 h-4 text-slate-200" />
                                                    )}
                                                </div>
                                                <span className={`block truncate ${isSelected ? "text-slate-800 font-black" : ""}`}>
                                                    {opt.label}
                                                </span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="p-6 text-center">
                                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">
                                            No results found
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
            {error && <span className='text-red-500 text-[10px] font-black uppercase tracking-tight mt-1 flex items-center gap-1.5'>{error}</span>}
        </div>
    );
};

export default MultiSelectDropdown;
